import { ColDef, ColGroupDef } from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import { StatementColumn } from '../types'
import { EnhancedStatementRow } from './dataEnhancement'
import { extractDataValue } from '../../../shared/utils/balanceRegistry'
import ValueCellRenderer from '../components/StatementTable/cellRenderers/ValueCellRenderer'
import MonthGroupHeaderRenderer from '../components/StatementTable/cellRenderers/MonthGroupHeaderRenderer'
import { formatValueByType } from '../../../utils/helpers'
import { VariableType } from '../../../components/Table/types'
import { ReportDataType } from '../../../redux/context/reports/types'
import { getBaseRowTypeCellClassRules } from './columnUtils'
import { SortOrder } from '../../../redux/context/filters/types'

interface MonthGroup {
  month: number
  year?: number
  label: string
  columns: StatementColumn[]
}

/**
 * Groups columns by year-month for AG Grid column grouping.
 * This supports fiscal years that span calendar years and can have 13 months.
 *
 * @param columns - Array of StatementColumn to group
 * @returns Map of year-month keys to MonthGroup
 */
export const groupColumnsByMonth = (columns: StatementColumn[]): Map<string, MonthGroup> => {
  const monthGroups = new Map<string, MonthGroup>()

  columns.forEach(column => {
    // Skip columns without month or year information
    if (column.month === undefined || column.year === undefined) {
      return
    }

    // Create a unique key for each year-month combination
    // For comparativePeriod columns, add 1 year so they appear alongside current period
    let { year } = column
    if (column.columnType === ReportDataType.comparativePeriod) {
      year += 1
    }
    const monthKey = `${year}-${String(column.month).padStart(2, '0')}`

    if (!monthGroups.has(monthKey)) {
      // Create month name for header using dayjs localized month names
      const monthNames = dayjs.localeData().months()
      const monthName = monthNames[column.month - 1] || ''

      // Add year to label (using adjusted year for comparativePeriod)
      const label = `${monthName} ${year}`

      monthGroups.set(monthKey, {
        month: column.month,
        year,
        label,
        columns: []
      })
    }

    const group = monthGroups.get(monthKey)!
    group.columns.push(column)
  })

  return monthGroups
}

// Helper to calculate cumulative sum for columns
const calculateCumulativeSum = (params: any, columns: StatementColumn[]): number => {
  let sum = 0
  for (const col of columns) {
    const value = extractDataValue(params.data, col)
    if (value !== null && value !== undefined) {
      sum += value
    }
  }
  return sum
}

// Helper to create cumulative column definition
const createCumulativeColDef = (
  columnType: ReportDataType,
  columns: StatementColumn[],
  month: number,
  year?: number
): ColDef<EnhancedStatementRow> => {
  const cellClass = `${columnType}-cumulative-column`

  // Format header based on column type
  let headerName: string
  if (columnType === ReportDataType.budget) {
    // Budget: add "B" after month/year
    const monthYear = year ? `${String(month).padStart(2, '0')}/${year}` : String(month).padStart(2, '0')
    headerName = `${monthYear} B cum.`
  } else if (columnType === ReportDataType.comparativePeriod) {
    // Comparative: subtract 1 year
    const compYear = year ? year - 1 : undefined
    const monthYear = compYear ? `${String(month).padStart(2, '0')}/${compYear}` : String(month).padStart(2, '0')
    headerName = `${monthYear} cum.`
  } else {
    // Actuals, Forecast: normal format
    const monthYear = year ? `${String(month).padStart(2, '0')}/${year}` : String(month).padStart(2, '0')
    headerName = `${monthYear} cum.`
  }

  return {
    colId: `cumulative-${columnType}-${month}`,
    headerName,
    headerClass: 'ag-right-aligned-header cumulative-header',
    minWidth: 80,
    flex: 1,
    cellDataType: 'number',
    columnGroupShow: 'open', // Only show when group is expanded
    cellRenderer: ValueCellRenderer,
    cellRendererParams: {
      showPercentage: false
    },
    valueFormatter: params => {
      if (params.value === undefined || params.value === null) return ''
      return formatValueByType(params.value, VariableType.absolute)
    },
    valueGetter: params => calculateCumulativeSum(params, columns),
    wrapText: true,
    cellClass,
    cellClassRules: {
      ...getBaseRowTypeCellClassRules()
    },
    cellStyle: () => ({
      textAlign: 'right' as const
    })
  }
}

/**
 * Creates AG Grid column group definitions from StatementColumns grouped by month.
 * Columns are grouped by month only, allowing comparative analysis across years.
 *
 * @param columns - Array of StatementColumn to group
 * @param createColDef - Function to create a ColDef from a StatementColumn
 * @returns Array of ColGroupDef and ColDef for columns without month info
 */
export const createColumnGroupDefs = <TData = EnhancedStatementRow>(
  columns: StatementColumn[],
  createColDef: (column: StatementColumn) => ColDef<TData>
): (ColDef<TData> | ColGroupDef<TData>)[] => {
  const monthGroups = groupColumnsByMonth(columns)
  const result: (ColDef<TData> | ColGroupDef<TData>)[] = []

  // Handle columns without month info first
  const columnsWithoutMonth = columns.filter(col => col.month === undefined)
  columnsWithoutMonth.forEach(column => {
    result.push(createColDef(column))
  })

  // Create column groups for each month
  // Sort by year first, then by month
  const sortedGroups = Array.from(monthGroups.values()).sort((a, b) => {
    if (a.year !== b.year) {
      return (a.year ?? 0) - (b.year ?? 0)
    }
    return a.month - b.month
  })

  sortedGroups.forEach(group => {
    const groupDef: ColGroupDef<TData> = {
      headerName: group.label,
      children: group.columns.map(column => createColDef(column)),
      // Optional: Add styling for month groups
      headerClass: 'month-group-header'
    }
    result.push(groupDef)
  })

  return result
}

/**
 * Alternative: Group by month with shorter month names and cumulative columns.
 * Groups by month only (ignoring year) for comparative analysis.
 * Adds cumulative columns that appear when the group is expanded.
 *
 * @param columns - Array of StatementColumn to group
 * @param createColDef - Function to create a ColDef from a StatementColumn
 * @param useShortNames - Use short month names (default: true)
 * @param includeCumulative - Include cumulative columns (default: true)
 * @param dataTypes - Available data types for filtering cumulative columns
 * @param order - Display order of month groups. Cumulatives are always calculated chronologically.
 */
export const createMonthOnlyColumnGroupDefs = <TData = EnhancedStatementRow>(
  columns: StatementColumn[],
  createColDef: (column: StatementColumn) => ColDef<TData>,
  useShortNames = true,
  includeCumulative = true,
  dataTypes?: ReportDataType[],
  order: SortOrder = 'asc'
): (ColDef<TData> | ColGroupDef<TData>)[] => {
  const monthGroups = groupColumnsByMonth(columns)
  const result: (ColDef<TData> | ColGroupDef<TData>)[] = []

  // Handle columns without month info first
  const columnsWithoutMonth = columns.filter(col => col.month === undefined)
  columnsWithoutMonth.forEach(column => {
    result.push(createColDef(column))
  })

  // Create column groups for each month
  // Sort by year first, then by month
  const sortedGroups = Array.from(monthGroups.values()).sort((a, b) => {
    if (a.year !== b.year) {
      return (a.year ?? 0) - (b.year ?? 0)
    }
    return a.month - b.month
  })

  // Track all columns up to current month for cumulative calculations
  const allColumnsUpToMonth: StatementColumn[] = []
  const monthGroupDefs: ColGroupDef<TData>[] = []

  sortedGroups.forEach(group => {
    // Add this group's columns to the cumulative list
    allColumnsUpToMonth.push(...group.columns)

    // Create regular columns
    const regularColumns = group.columns.map(column => createColDef(column))

    // Create cumulative columns if enabled
    const cumulativeColumns: ColDef<TData>[] = []
    if (includeCumulative) {
      // Group columns by columnType (ReportDataType)
      const columnsByType = new Map<ReportDataType, StatementColumn[]>()

      allColumnsUpToMonth.forEach(col => {
        if (!columnsByType.has(col.columnType)) {
          columnsByType.set(col.columnType, [])
        }
        columnsByType.get(col.columnType)!.push(col)
      })

      // Filter by dataTypes if provided
      const typesToInclude = dataTypes && dataTypes.length > 0 ? dataTypes : Array.from(columnsByType.keys())

      // Create cumulative column for each type present in this month
      typesToInclude.forEach(columnType => {
        const columnsOfType = columnsByType.get(columnType)
        if (columnsOfType && columnsOfType.length > 0) {
          cumulativeColumns.push(
            createCumulativeColDef(columnType, columnsOfType, group.month, group.year) as any as ColDef<TData>
          )
        }
      })

      // Add difference columns if applicable
      const actualsColumns = columnsByType.get(ReportDataType.actuals)
      const budgetColumns = columnsByType.get(ReportDataType.budget)
      const comparativeColumns = columnsByType.get(ReportDataType.comparativePeriod)

      // Actuals - Budget difference
      if (
        actualsColumns &&
        actualsColumns.length > 0 &&
        budgetColumns &&
        budgetColumns.length > 0 &&
        (!dataTypes || (dataTypes.includes(ReportDataType.actuals) && dataTypes.includes(ReportDataType.budget)))
      ) {
        const monthYear = group.year
          ? `${String(group.month).padStart(2, '0')}/${group.year}`
          : String(group.month).padStart(2, '0')

        cumulativeColumns.push({
          colId: `cumulative-difference-${group.month}`,
          headerName: `${monthYear} B diff.`,
          headerClass: 'ag-right-aligned-header cumulative-header',
          minWidth: 80,
          flex: 1,
          cellDataType: 'number',
          columnGroupShow: 'open',
          cellRenderer: ValueCellRenderer,
          cellRendererParams: {
            showPercentage: false
          },
          valueFormatter: (params: any) => {
            if (params.value === undefined || params.value === null) return ''
            return formatValueByType(params.value, VariableType.absolute)
          },
          valueGetter: (params: any) => {
            const actualsSum = calculateCumulativeSum(params, actualsColumns)
            const budgetSum = calculateCumulativeSum(params, budgetColumns)
            return actualsSum - budgetSum
          },
          wrapText: true,
          cellClass: 'diff-column',
          cellClassRules: {
            ...getBaseRowTypeCellClassRules()
          } as any,
          cellStyle: () => ({
            textAlign: 'right' as const
          })
        } as any as ColDef<TData>)
      }

      // Actuals - Comparative difference
      if (
        actualsColumns &&
        actualsColumns.length > 0 &&
        comparativeColumns &&
        comparativeColumns.length > 0 &&
        (!dataTypes ||
          (dataTypes.includes(ReportDataType.actuals) && dataTypes.includes(ReportDataType.comparativePeriod)))
      ) {
        const compYear = group.year ? group.year - 1 : undefined
        const compMonthYear = compYear
          ? `${String(group.month).padStart(2, '0')}/${compYear}`
          : String(group.month).padStart(2, '0')

        cumulativeColumns.push({
          colId: `cumulative-comparative-difference-${group.month}`,
          headerName: `${compMonthYear} diff.`,
          headerClass: 'ag-right-aligned-header cumulative-header',
          minWidth: 80,
          flex: 1,
          cellDataType: 'number',
          columnGroupShow: 'open',
          cellRenderer: ValueCellRenderer,
          cellRendererParams: {
            showPercentage: false
          },
          valueFormatter: (params: any) => {
            if (params.value === undefined || params.value === null) return ''
            return formatValueByType(params.value, VariableType.absolute)
          },
          valueGetter: (params: any) => {
            const actualsSum = calculateCumulativeSum(params, actualsColumns)
            const comparativeSum = calculateCumulativeSum(params, comparativeColumns)
            return actualsSum - comparativeSum
          },
          wrapText: true,
          cellClass: 'comparative-diff-column',
          cellClassRules: {
            ...getBaseRowTypeCellClassRules()
          } as any,
          cellStyle: () => ({
            textAlign: 'right' as const
          })
        } as any as ColDef<TData>)
      }
    }

    const groupDef: ColGroupDef<TData> = {
      groupId: `month-group-${group.year ?? ''}-${group.month}`,
      headerGroupComponent: MonthGroupHeaderRenderer,
      children: [...regularColumns, ...cumulativeColumns],
      headerClass: 'month-group-header',
      openByDefault: false // Start closed so cumulative columns are hidden
    }
    monthGroupDefs.push(groupDef)
  })

  result.push(...(order === 'desc' ? monthGroupDefs.reverse() : monthGroupDefs))

  return result
}
